"use client";

import { useEffect, useRef, useState } from "react";
import { X, ImagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { usePartnersSectionStore } from "@/store/partnersSectionStore";
import { ActivityLogger } from "@/lib/activityLog";
import { prepareImageUpload } from "@/lib/image-upload";
import { getStrapiImageUrl } from "@/lib/image-utils";

interface CreatePartnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
}

type TierKey = 'strategic' | 'aligned' | 'awareness';
const TIERS: { key: TierKey; field: 'strategicImage' | 'alignedImage' | 'awarenessImage'; label: string; testIdPrefix: string }[] = [
  { key: 'strategic', field: 'strategicImage', label: 'Strategic Partners', testIdPrefix: 'create-partners-strategic' },
  { key: 'aligned',   field: 'alignedImage',   label: 'Aligned Partners',   testIdPrefix: 'create-partners-aligned' },
  { key: 'awareness', field: 'awarenessImage', label: 'Awareness Partners', testIdPrefix: 'create-partners-awareness' },
];

export default function CreatePartnersModal({ isOpen, onClose, onResult }: CreatePartnersModalProps) {
  const { currentUser } = useAuth();
  const { partnersSection, saving, fetchPartnersSection, updatePartnersSection } = usePartnersSectionStore();

  const [pending, setPending] = useState<Record<TierKey, File | null>>({ strategic: null, aligned: null, awareness: null });
  const [previews, setPreviews] = useState<Record<TierKey, string | null>>({ strategic: null, aligned: null, awareness: null });
  // Mirror of the live preview object URLs so cleanup is not tied to a stale state closure.
  const previewUrlsRef = useRef<Record<TierKey, string | null>>({ strategic: null, aligned: null, awareness: null });

  const setPreview = (tier: TierKey, url: string | null) => {
    const previous = previewUrlsRef.current[tier];
    if (previous) URL.revokeObjectURL(previous);
    previewUrlsRef.current = { ...previewUrlsRef.current, [tier]: url };
    setPreviews((p) => ({ ...p, [tier]: url }));
  };

  const revokeAllPreviews = () => {
    for (const url of Object.values(previewUrlsRef.current)) {
      if (url) URL.revokeObjectURL(url);
    }
    previewUrlsRef.current = { strategic: null, aligned: null, awareness: null };
  };

  useEffect(() => {
    if (isOpen) {
      fetchPartnersSection();
      setPending({ strategic: null, aligned: null, awareness: null });
      setPreviews({ strategic: null, aligned: null, awareness: null });
    }
    return () => revokeAllPreviews();
  }, [isOpen, fetchPartnersSection]);

  if (!isOpen) return null;

  const previewFor = (tier: TierKey): string | null => {
    if (previews[tier]) return previews[tier];
    const field = TIERS.find((t) => t.key === tier)!.field;
    const saved = partnersSection?.[field];
    return saved?.url ? getStrapiImageUrl(saved.url) : null;
  };

  const handleFile = async (tier: TierKey, file: File | null) => {
    if (!file) {
      setPending((p) => ({ ...p, [tier]: null }));
      setPreview(tier, null);
      return;
    }
    const { file: prepared, error } = await prepareImageUpload(file);
    if (error) {
      onResult?.(false, error);
      return;
    }
    setPending((p) => ({ ...p, [tier]: prepared }));
    setPreview(tier, URL.createObjectURL(prepared));
  };

  const hasChanges = pending.strategic !== null || pending.aligned !== null || pending.awareness !== null;

  const handleSubmit = async () => {
    if (!hasChanges || saving) return;
    try {
      const result = await updatePartnersSection({
        strategicImage: pending.strategic ?? undefined,
        alignedImage: pending.aligned ?? undefined,
        awarenessImage: pending.awareness ?? undefined,
      });
      if (result) {
        onResult?.(true, 'Partners section updated successfully');
        if (currentUser) {
          ActivityLogger.editPartnersSection(
            currentUser,
            TIERS.filter((tier) => pending[tier.key] !== null).map((tier) => tier.label),
          );
        }
        onClose();
      } else {
        onResult?.(false, 'Failed to save partners section');
      }
    } catch (err) {
      console.error('Failed to update partners section:', err);
      const message = err instanceof Error ? err.message : 'An error occurred';
      onResult?.(false, message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="create-partners-root" className="relative bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <h2 className="text-lg font-semibold text-foreground">Edit Partners Section</h2>
          <button data-testid="create-partners-close-btn" onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {TIERS.map(({ key, label, testIdPrefix }) => (
            <PartnerImageField
              key={key}
              label={label}
              previewUrl={previewFor(key)}
              hasPending={pending[key] !== null}
              onChange={(f) => { void handleFile(key, f); }}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button
            data-testid="create-partners-cancel-btn"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="create-partners-submit-btn"
            onClick={handleSubmit}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PartnerImageField({
  label,
  previewUrl,
  hasPending,
  onChange,
  testIdPrefix,
}: {
  label: string;
  previewUrl: string | null;
  hasPending: boolean;
  onChange: (file: File | null) => void;
  testIdPrefix: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const openPicker = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      {previewUrl ? (
        <div className="relative inline-block rounded-md overflow-hidden border border-border bg-background-secondary group">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={previewUrl}
            alt=""
            data-testid={`${testIdPrefix}-preview`}
            className="block max-w-full max-h-48 cursor-pointer"
            onClick={openPicker}
          />
          {hasPending ? (
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid={`${testIdPrefix}-discard-pending-btn`}
              aria-label="Discard pending image"
              className="absolute top-2 right-2 z-10 rounded-full bg-red-500 p-1 text-white hover:bg-red-600 transition"
            >
              <X size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={openPicker}
              data-testid={`${testIdPrefix}-replace-btn`}
              aria-label="Replace image"
              className="absolute top-2 right-2 z-10 rounded-full bg-primary p-1.5 text-background hover:opacity-90 transition"
            >
              <ImagePlus size={14} />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          data-testid={`${testIdPrefix}-drop-zone-btn`}
          className="w-full border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-primary/50 transition flex flex-col items-center gap-1.5"
        >
          <ImagePlus size={32} className="text-foreground-muted" />
          <span className="text-sm text-foreground-muted">Click to upload a photo</span>
          <span className="text-xs text-caption">PNG, JPG</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        data-testid={`${testIdPrefix}-input`}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        className="hidden"
      />
    </div>
  );
}
